<template>
    <div
        :class="[$style['mds-data-table'], classObject, paginationObject, onDarkObject, sizeClass]"
        :data-mds-version="version"
    >
        <slot name="above" />
        <mds-pagination
            ref="paginate"
            v-if="pagination === 'above'"
            :size="size"
            :on-dark="onDark"
            v-bind="paginationConfig"
            @mds-pagination-page-changed="paginateTable(arguments)"
        />
        <div :class="[responsiveObject]" role="group" ref="container">
            <span
                v-if="fixedFirstCol"
                ref="responsiveBefore"
                :class="[$style['mds-data-table__responsive-wrap-span--begin']]"
            />
            <div :class="[$style['mds-data-table__scroller']]" ref="scroller" :tabindex="tabIndex">
                <table :class="[$style['mds-data-table__table']]" ref="table">
                    <caption :class="[$style['mds-data-table__caption']]">
                        {{
                            caption
                        }}
                    </caption>
                    <slot name="header">
                        <mds-data-table-header
                            ref="header"
                            :header-configs="headerConfigs"
                            @mds-data-table-header-action-click="emitHeaderActionClicked(arguments)"
                            @mds-data-table-sort-change="emitHeaderSortChanged(arguments, 'header')"
                            @mds-data-table-header-select-all="emitHeaderSelectAll(arguments)"
                        >
                            <template v-slot:header-cell="headerCellData">
                                <slot name="header-cell" v-bind="headerCellData" />
                            </template>
                            <template v-slot:action-cell="colIndex">
                                <slot name="action-cell" v-bind="colIndex" />
                            </template>
                        </mds-data-table-header>
                    </slot>
                    <slot name="body">
                        <mds-data-table-body
                            ref="body"
                            :header-configs="headerConfigs"
                            :row-data="rowData"
                            @mds-data-table-row-selection-change="selectChanged(arguments)"
                            @mds-data-table-row-hover-change="setRowHover(arguments)"
                            @mds-data-table-row-click="setRowClick(arguments)"
                        >
                            <template v-slot:body-cell="cellData">
                                <slot name="body-cell" v-bind="cellData" />
                            </template>
                        </mds-data-table-body>
                    </slot>
                    <tfoot>
                        <slot name="footer" />
                    </tfoot>
                </table>
            </div>
            <div
                v-if="sticky"
                :class="[$style[reponsiveTableModifier]]"
                ref="stickyColumn"
                :style="{ width: stickyColWidth }"
            >
                <table
                    :class="[$style['mds-data-table__table']]"
                    ref="stickyTable"
                    :style="{ width: `${tableWidth}px` }"
                >
                    <slot name="header">
                        <mds-data-table-header
                            ref="stickyHeader"
                            :header-configs="headerConfigs"
                            @mds-data-table-header-action-click="emitHeaderActionClicked(arguments)"
                            @mds-data-table-sort-change="emitHeaderSortChanged(arguments, 'stickyHeader')"
                            @mds-data-table-header-select-all="emitHeaderSelectAll(arguments)"
                        >
                            <template v-slot:header-cell="headerCellData">
                                <slot name="header-cell" v-bind="headerCellData" />
                            </template>
                            <template v-slot:action-cell="actionCol">
                                <slot name="action-cell" v-bind="actionCol" />
                            </template>
                        </mds-data-table-header>
                    </slot>
                    <slot name="body">
                        <mds-data-table-body
                            ref="stickyBody"
                            :header-configs="headerConfigs"
                            :row-data="rowData"
                            @mds-data-table-row-selection-change="selectChanged(arguments)"
                            @mds-data-table-row-hover-change="setRowHover(arguments)"
                            @mds-data-table-row-click="setRowClick(arguments)"
                        >
                            <template v-slot:body-cell="cellData">
                                <slot name="body-cell" v-bind="cellData" />
                            </template>
                        </mds-data-table-body>
                    </slot>
                    <tfoot>
                        <slot name="footer" />
                    </tfoot>
                </table>
            </div>
        </div>
        <mds-pagination
            ref="paginate"
            v-if="pagination === 'below'"
            :size="size"
            :on-dark="onDark"
            v-bind="paginationConfig"
            @mds-pagination-page-changed="paginateTable(arguments)"
        />
        <slot name="below" />
    </div>
</template>

<script>
    import MdsDataTableHeader from './data-table-header/data-table-header.vue';
    import MdsDataTableBody from './data-table-body/data-table-body.vue';
    import MdsPagination from '@mds/pagination';
    import { MdsPositioning } from '@mds/utils-js';
    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsDataTable',
        components: {
            MdsDataTableHeader,
            MdsDataTableBody,
            MdsPagination,
        },
        props: {
            caption: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the table caption, describes purpose of table.',
                },
            },
            fixedFirstCol: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        "If `true`, table will fix first column in place. You may customize the column's width in `headerConfigs`.",
                },
            },
            headerConfigs: {
                type: Array,
                required: true,
                docs: {
                    validation: '—',
                    description:
                        'Set of objects that describe each header column. See the header cell props for a definition of all properties.',
                },
            },
            rowData: {
                type: Array,
                required: true,
                docs: {
                    validation: '—',
                    description: 'Sets the data items to display in row cells.',
                },
            },
            multiselection: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, shows the select checkboxes in both the header and rows (if using default slot content).',
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            pagination: {
                type: String,
                validator(value) {
                    return ['above', 'below'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `above`, `below`.',
                    description: 'Sets the table to paginate. Should be used with `paginationConfig`.',
                },
            },
            paginationConfig: {
                type: Object,
                docs: {
                    validation: '—',
                    description:
                        'Sets pagination options. See pagination component props for a definition of all properties.',
                },
            },
            responsive: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, table will switch to responsive style when table content exceeds container width. **This prop has been deprecated. Instead, use `fixedFirstCol`.**{.mds-doc-prop-table__deprecated-text}',
                    deprecated: true,
                },
            },
            rowHover: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, hover styling applied to row on hover.',
                },
            },
            rowNumbers: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, row numbers are visible for each row.',
                },
            },
            showSortable: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sortable indicator is visible in header.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`.',
                    description: 'Sets the size of the table.',
                },
            },
            spacious: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, applies padding to cells.',
                },
            },
            version: {
                type: String,
                default: `${name}-${version}`,
                docs: {
                    validation: 'Read-only',
                    description:
                        'Set automatically by package version, do not manually adjust this prop. Used to add a data attribute providing the component version to the component markup rendered in the DOM.',
                },
            },
            zebraStripes: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, applies zebra stripe styling to rows.',
                },
            },
        },
        mixins: [MdsPositioning],
        provide() {
            // Properties that are available for injection into components descendants https://vuejs.org/v2/api/#provide-inject
            const parentVariation = {};
            Object.defineProperty(parentVariation, 'multiSelect', {
                enumerable: true,
                get: () => this.multiselection,
            });
            Object.defineProperty(parentVariation, 'rowNumber', {
                enumerable: true,
                get: () => this.rowNumbers,
            });
            Object.defineProperty(parentVariation, 'responsive', {
                enumerable: true,
                get: () => this.responsive,
            });
            Object.defineProperty(parentVariation, 'style', {
                get: () => this.$style,
            });
            return { parentVariation };
        },
        data() {
            return {
                indeterminate: false,
                allChecked: false,
                beginVisible: true,
                endVisible: true,
                tableIds: [],
                tableWidth: 0,
                stickyColWidth: null,
            };
        },
        computed: {
            classObject() {
                const obj = {};
                obj[this.$style['mds-data-table--show-sortable']] = this.showSortable;
                obj[this.$style['mds-data-table--row-hover']] = this.rowHover;
                obj[this.$style['mds-data-table--zebra']] = this.zebraStripes;
                obj[this.$style['mds-data-table--spacious']] = this.spacious;

                return obj;
            },
            onDarkObject() {
                const obj = {};
                obj[this.$style['mds-component--on-dark']] = this.onDark;

                return obj;
            },
            responsiveObject() {
                const showFixedCol = this.responsive || this.fixedFirstCol;
                const obj = {};
                obj[this.$style['mds-data-table__responsive-wrap']] = showFixedCol;
                obj[this.$style['mds-data-table__responsive-wrap--end']] = showFixedCol && this.endVisible;
                obj[this.$style['mds-data-table__responsive-wrap--begin']] = showFixedCol && this.beginVisible;
                obj[this.$style['mds-data-table__responsive-wrap--fixed']] = this.fixedFirstCol;

                return obj;
            },
            paginationObject() {
                const obj = {};
                obj[this.$style[`mds-data-table__${this.pagination}`]] = this.pagination;

                return obj;
            },
            sizeClass() {
                return this.$style[`mds-data-table--${this.size}`];
            },
            tabIndex() {
                return this.responsive ? '0' : null;
            },
            reponsiveTableModifier() {
                return this.responsive ? 'mds-data-table--sticky-column' : 'mds-data-table--fixed-column';
            },
            sticky() {
                return this.responsive || this.fixedFirstCol;
            },
        },
        watch: {
            headerConfigs(newValue) {
                const noWidth = newValue[0].width === undefined || newValue[0].width === null;
                if (noWidth) {
                    this.stickyColWidth = null;
                } else {
                    this.stickyColWidth = this.fixedFirstCol ? newValue[0].width : null;
                }
            },
            rowData() {
                if (this.multiselection && this.$refs.body) {
                    this.updateHeaderCheck();
                }
            },
            responsive(newValue) {
                // TODO: deprecate when removing data table responsive
                if (newValue) {
                    this.checkResponsiveWidth();
                    window.addEventListener('resize', this.debounce(this.responsiveShadow, 100));
                    this.$refs.scroller.addEventListener('scroll', this.responsiveShadow);
                    this.responsiveShadow();
                } else {
                    this.$refs.scroller.removeEventListener('scroll', this.responsiveShadow);
                    window.removeEventListener('resize', this.debounce(this.responsiveShadow, 100));
                }
            },
            fixedFirstCol(newValue) {
                if (newValue) {
                    this.checkResponsiveWidth();
                    window.addEventListener('resize', this.debounce(this.responsiveShadow, 100));
                    this.$refs.scroller.addEventListener('scroll', this.responsiveShadow);
                    this.responsiveShadow();
                } else {
                    this.$refs.scroller.removeEventListener('scroll', this.responsiveShadow);
                    window.removeEventListener('resize', this.debounce(this.responsiveShadow, 100));
                }
            },
            rowNumbers() {
                if (this.fixedFirstCol || this.responsive) {
                    this.checkResponsiveWidth();
                }
            },
            multiselection() {
                if (this.responsive || this.fixedFirstCol) {
                    this.checkResponsiveWidth();
                }
            },
        },
        methods: {
            emitHeaderActionClicked(ev) {
                this.$emit('mds-data-table-header-action-click', ev[0]);
            },
            paginateTable(ev) {
                const pageInfo = ev[0];
                this.$emit('mds-data-table-page-change', pageInfo);
            },
            emitHeaderSortChanged(ev, header) {
                if (this.sticky) {
                    const headerRef = header === 'header' ? this.$refs.stickyHeader : this.$refs.header;
                    headerRef.sortField = ev[0].sortField;
                    headerRef.sortOrder = ev[0].sortOrder;
                    headerRef.updateCellsOnSort();
                }
                this.$emit('mds-data-table-sort-change', ev[0]);
            },
            selectChanged(ev) {
                if (this.sticky) {
                    this.$refs.body.$refs.row.filter(row => {
                        if (row.id === ev[0].id) {
                            row.selected = ev[0].checked;
                        }
                    });
                    this.$refs.body.updateBodySelectedRows(ev[0]);
                }
                if (this.multiselection && this.$refs.body) {
                    this.updateHeaderCheck();
                }
                this.$emit('mds-data-table-row-selection-change', ev[0]);
            },
            updateHeaderCheck() {
                this.getTableRowIds();
                const intersection = this.$refs.body
                    ? this.tableIds.filter(id => this.$refs.body.dataSelectedRows.includes(id))
                    : [];
                this.indeterminate = intersection.length > 0 && intersection.length < this.rowData.length;
                this.allChecked = intersection.length > 0 && intersection.length === this.rowData.length;
                if (this.$refs.header) {
                    this.$refs.header.isChecked = this.allChecked;
                    if (this.indeterminate) {
                        this.$refs.header.isChecked = false;
                    }
                    this.$refs.header.setIndeterminate = this.indeterminate;
                }

                if (this.$refs.stickyHeader) {
                    this.$refs.stickyHeader.isChecked = this.allChecked;
                    if (this.indeterminate) {
                        this.$refs.stickyHeader.isChecked = false;
                    }
                    this.$refs.stickyHeader.setIndeterminate = this.indeterminate;
                }
            },
            emitHeaderSelectAll(checkAll) {
                this.$emit('mds-data-table-header-select-all', checkAll[0]);
            },
            getTableRowIds() {
                this.tableIds = [];
                this.rowData.forEach(data => {
                    this.tableIds.push(data.id);
                });
            },
            setRowClick(ev) {
                this.$emit('mds-data-table-row-click', ev[0]);
            },
            setRowHover(ev) {
                const hover = ev[0].hover;
                const index = ev[0].rowIndex;
                const bodyRow = this.$refs.body.$refs.row[index];
                bodyRow.isHover = hover;
                if (this.sticky) {
                    const stickyBodyRow = this.$refs.stickyBody.$refs.row[index];
                    stickyBodyRow.isHover = hover;
                }
                this.$emit('mds-data-table-row-hover-change', ev[0]);
            },
            checkResponsiveWidth() {
                const isMultiselect = this.multiselection && !this.rowNumbers;
                const isNumbered = !this.multiselection && this.rowNumbers;
                const isBoth = this.multiselection && this.rowNumbers;
                const defaultCheck = isMultiselect || isNumbered ? 20 : 0;
                const checkboxWidth = isBoth ? 40 : defaultCheck;
                this.tableWidth = this.$refs.table.clientWidth;
                const noWidth = this.headerConfigs[0].width === undefined || this.headerConfigs[0].width === null;
                if (noWidth) {
                    this.stickyColWidth = null;
                } else {
                    this.stickyColWidth = this.fixedFirstCol
                        ? `${parseInt(this.headerConfigs[0].width) + checkboxWidth}px`
                        : null;
                }

                if (this.fixedFirstCol && this.$refs.responsiveBefore) {
                    this.$refs.responsiveBefore.style.left = `${parseInt(this.stickyColWidth) + checkboxWidth - 5}px`;
                }
            },
            responsiveShadow() {
                const tableEl = this.$refs['table'];
                const containerEl = this.$refs['container'];
                const scrollerEl = this.$refs['scroller'];
                var containerRect, tableRect;
                var sizeDiff = 0;

                if (containerEl !== undefined) {
                    containerRect = containerEl.getBoundingClientRect();
                    tableRect = tableEl.getBoundingClientRect();
                    sizeDiff = Math.floor(tableRect.width - containerRect.width);
                    if (this.fixedFirstCol && this.$refs.responsiveBefore) {
                        this.$refs.responsiveBefore.style.left = `${parseInt(this.stickyColWidth) - 5}px`;
                    }
                    this.endVisible = sizeDiff >= 0 && Math.abs(Math.round(scrollerEl.scrollLeft) - sizeDiff) <= 1;
                    this.beginVisible = scrollerEl.scrollLeft <= 0;
                }
            },
        },
        mounted() {
            if (this.multiselection && this.$refs.body) {
                this.updateHeaderCheck();
            }
            if (this.responsive || this.fixedFirstCol) {
                this.checkResponsiveWidth();
                this.responsiveShadow();
                this.$refs.scroller.addEventListener('scroll', this.responsiveShadow);
                window.addEventListener('resize', this.debounce(this.responsiveShadow, 100, true));
            }
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.debounce(this.responsiveShadow, 100));
        },
    };
</script>

<style lang="scss" module>
    @import './data-table.scss';
</style>
